import { natsClient } from './NATSClient';
import { isDemoMode } from './demoMode';

class NavigationCrypto {
  private static async getKey(userId: string): Promise<CryptoKey> {
    // In production, derive this from user session or secure token
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(userId.padEnd(32, '0').slice(0, 32)),
      { name: 'PBKDF2' },
      false,
      ['deriveKey']
    );

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: new TextEncoder().encode('navigation-salt'),
        iterations: 1000,
        hash: 'SHA-256'
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  }

  static async encrypt(data: any, userId: string): Promise<string> {
    const key = await this.getKey(userId);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encoded = new TextEncoder().encode(JSON.stringify(data));
    
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      encoded
    );

    // Combine IV and encrypted data
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);
    
    return btoa(String.fromCharCode(...combined));
  }

  static async decrypt(encryptedData: string, userId: string): Promise<any> {
    const key = await this.getKey(userId);
    const combined = new Uint8Array(
      atob(encryptedData).split('').map(c => c.charCodeAt(0))
    );
    
    const iv = combined.slice(0, 12);
    const encrypted = combined.slice(12);
    
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      encrypted
    );
    
    return JSON.parse(new TextDecoder().decode(decrypted));
  }
}

export interface CachedMachineData {
  maschinennummer?: string;
  maschineBK?: string;
  bautyp?: string;
  machineName?: string;
  machineType?: string;
  lineId?: string;
  lineName?: string;
  factoryName?: string;
  datenVerfuegbar?: boolean;
  cmVerfuegbar?: boolean;
}

export interface NavigationEvent {
  type: 'machine' | 'line';
  id: string;
  name?: string;
  timestamp: number;
  sessionId: string;
  userId?: string;
  machineData?: CachedMachineData;
}

export class NavigationHistoryService {
  private static readonly STREAM_NAME = 'USER_NAVIGATION';
  private static readonly SUBJECT_PREFIX = 'user.navigation';
  private static readonly LOCAL_STORAGE_KEY_PREFIX = 'navigation_history_';
  
  // Sanitize username for use in NATS consumer names (remove dots and special chars)
  // NATS consumer names cannot contain dots, @, or most special characters
  public static sanitizeUsernameForConsumer(username: string): string {
    return username
      .toLowerCase()
      .replace(/[^a-z0-9\-_]/g, '_')  // Replace all non-alphanumeric except - and _ with _
      .replace(/_+/g, '_')            // Replace multiple consecutive underscores with single
      .replace(/^_|_$/g, '')          // Remove leading/trailing underscores
      .slice(0, 32);                  // Limit length for NATS consumer name requirements
  }
  
  /**
   * Navigation history is per-user state on the real account. In demo mode it
   * is disabled outright rather than left to fail: the demo NATS account may
   * not be granted `user.navigation.*`, and every visit would log an error.
   */
  private static get disabled(): boolean {
    return isDemoMode();
  }

  static async initialize() {
    if (this.disabled) return;
    try {
      if (natsClient.isConnected()) {
        console.log('NATS connected, initializing navigation history service');
        
        // Create JetStream stream for navigation history if it doesn't exist
        try {
          const jsm = natsClient.jetstreamManager();
          
          // Check if stream exists
          await jsm.streams.info(this.STREAM_NAME).catch(async () => {
            // Create stream if it doesn't exist
            await jsm.streams.add({
              name: this.STREAM_NAME,
              subjects: [`${this.SUBJECT_PREFIX}.*`],
              retention: 'limits' as any,
              max_msgs_per_subject: 1000,
              max_age: 90 * 24 * 60 * 60 * 1000000000, // 90 days in nanoseconds
              storage: 'file' as any
            });
            console.log(`Created JetStream stream: ${this.STREAM_NAME}`);
          });
        } catch (error) {
          console.error('Error setting up JetStream stream:', error);
        }
      } else {
        console.warn('NATS not connected, navigation history will be stored locally only');
      }
    } catch (error) {
      console.log('Error initializing navigation history service:', error);
    }
  }

  // Get history from local storage (public accessor for hook use)
  static getLocalHistoryPublic(userId: string): NavigationEvent[] {
    if (this.disabled) return [];
    return this.getLocalHistory(userId);
  }

  // Get history from local storage
  private static getLocalHistory(userId: string): NavigationEvent[] {
    try {
      const key = `${this.LOCAL_STORAGE_KEY_PREFIX}${userId}`;
      const stored = localStorage.getItem(key);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Failed to retrieve local history:', error);
    }
    return [];
  }

  // Save history to local storage
  private static saveLocalHistory(userId: string, events: NavigationEvent[]): void {
    try {
      const key = `${this.LOCAL_STORAGE_KEY_PREFIX}${userId}`;
      localStorage.setItem(key, JSON.stringify(events.slice(0, 20))); // Limit to 20 items
    } catch (error) {
      console.error('Failed to save local history:', error);
    }
  }

  static async recordVisit(
    type: 'machine' | 'line',
    id: string,
    name: string,
    userId: string,
    sessionId: string,
    machineData?: CachedMachineData
  ) {
    if (this.disabled) return;
    if (!userId) {
      console.warn('Cannot record navigation: no user ID');
      return;
    }

    const event: NavigationEvent = {
      type,
      id,
      name,
      timestamp: Date.now(),
      sessionId,
      userId,
      machineData
    };

    try {
      // Save to local storage first (synchronous and fast)
      const localHistory = this.getLocalHistory(userId);
      
      // Simple deduplication: remove existing entry for same type+id
      const filteredHistory = localHistory.filter(e => 
        !(e.type === type && e.id === id)
      );
      
      const updatedHistory = [event, ...filteredHistory].slice(0, 20);
      this.saveLocalHistory(userId, updatedHistory);
      
      // Publish to NATS in background (don't await)
      if (natsClient.isConnected()) {
        this.publishToNATS(event, userId).catch(error => {
          console.error('Background NATS publish failed:', error);
        });
      }
      
      // Reduced logging for better performance - only log in console if needed
    } catch (error) {
      console.error('Failed to record navigation:', error);
    }
  }

  // Separate method for NATS publishing (non-blocking)
  private static async publishToNATS(event: NavigationEvent, userId: string) {
    try {
      // Encrypt the event data
      const encryptedData = await NavigationCrypto.encrypt(event, userId);
      
      // Publish to user-specific subject
      const subject = `${this.SUBJECT_PREFIX}.${userId}`;
      await natsClient.publish(subject, encryptedData);
      
    } catch (error) {
      console.error('Failed to publish to NATS:', error);
      throw error;
    }
  }

  static async getHistory(userId: string, limit: number = 20): Promise<NavigationEvent[]> {
    if (this.disabled) return [];
    if (!userId) {
      console.warn('Cannot get history: no user ID');
      return [];
    }

    // Always start with local history for instant response
    const localHistory = this.getLocalHistory(userId);
    console.log(`💾 Local history contains ${localHistory.length} items`);
    
    // If NATS is not connected, just return local history
    if (!natsClient.isConnected()) {
      console.log(`📴 NATS not connected, returning local history only`);
      return localHistory.slice(0, limit);
    }

    // If we have recent local data, return it immediately and sync in background
    const hasRecentData = localHistory.length > 0 && 
      localHistory[0].timestamp > Date.now() - 300000; // 5 minutes

    if (hasRecentData) {
      // Return local data immediately and sync NATS in background
      this.syncWithNATS(userId, limit).catch(error => {
        console.error('Background NATS sync failed:', error);
      });
      return localHistory.slice(0, limit);
    }

    // If no recent local data, fetch from NATS with timeout
    console.log(`📡 NATS connected, fetching from JetStream...`);
    
    try {
      const natsEvents = await Promise.race([
        this.fetchFromNATS(userId, limit),
        new Promise<NavigationEvent[]>((_, reject) => 
          setTimeout(() => reject(new Error('NATS fetch timeout')), 3000)
        )
      ]);

      const mergedEvents = this.mergeAndDeduplicateEvents(natsEvents, localHistory, limit);
      this.saveLocalHistory(userId, mergedEvents);
      return mergedEvents;
    } catch (error) {
      console.error('NATS fetch failed or timed out, using local history:', error);
      return localHistory.slice(0, limit);
    }
  }

  // Background sync method (non-blocking)
  private static async syncWithNATS(userId: string, limit: number) {
    try {
      const natsEvents = await this.fetchFromNATS(userId, limit);
      const localHistory = this.getLocalHistory(userId);
      const mergedEvents = this.mergeAndDeduplicateEvents(natsEvents, localHistory, limit);
      this.saveLocalHistory(userId, mergedEvents);
    } catch (error) {
      console.error('Background NATS sync failed:', error);
    }
  }

  // Extract NATS fetching logic
  private static async fetchFromNATS(userId: string, limit: number): Promise<NavigationEvent[]> {
    const js = natsClient.jetstream();
    
    // Create consumer if it doesn't exist
    const sanitizedUsername = this.sanitizeUsernameForConsumer(userId);
    
    const consumerConfig = {
      durable_name: `navigation-${sanitizedUsername}`,
      filter_subject: `${this.SUBJECT_PREFIX}.${userId}`,
      deliver_policy: 'all' as any,
      ack_policy: 'explicit' as any
    };
    
    const jsm = natsClient.jetstreamManager();
    
    // Ensure stream exists
    await jsm.streams.info(this.STREAM_NAME).catch(() => {
      throw new Error('Stream does not exist');
    });
    
    // Try to get or create consumer
    await jsm.consumers.info(this.STREAM_NAME, `navigation-${sanitizedUsername}`).catch(async () => {
      await jsm.consumers.add(this.STREAM_NAME, consumerConfig);
    });
    
    // Get consumer and fetch messages
    const consumer = await js.consumers.get(this.STREAM_NAME, `navigation-${sanitizedUsername}`);
    const messages = await consumer.fetch({ max_messages: limit * 2 }); // Fetch more to account for duplicates
    
    const events: NavigationEvent[] = [];
    for await (const msg of messages) {
      try {
        const encryptedData = new TextDecoder().decode(msg.data);
        const event = await NavigationCrypto.decrypt(encryptedData, userId);
        events.push(event);
        msg.ack();
      } catch (error) {
        console.error('Failed to decrypt navigation event:', error);
        msg.ack();
      }
    }
    
    return events;
  }

  // Improved merge and deduplication logic
  private static mergeAndDeduplicateEvents(
    natsEvents: NavigationEvent[], 
    localEvents: NavigationEvent[], 
    limit: number
  ): NavigationEvent[] {
    // Combine all events
    const allEvents = [...natsEvents, ...localEvents];
    
    // Create a map with composite key for deduplication
    const eventMap = new Map<string, NavigationEvent>();
    
    for (const event of allEvents) {
      const key = `${event.type}-${event.id}`;
      const existing = eventMap.get(key);
      
      // Keep the event with the latest timestamp
      if (!existing || event.timestamp > existing.timestamp) {
        eventMap.set(key, event);
      }
    }
    
    // Convert back to array, sort by timestamp (newest first), and limit
    const uniqueEvents = Array.from(eventMap.values())
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, limit);
    
    return uniqueEvents;
  }

  static async clearHistory(userId: string) {
    if (this.disabled) return;
    if (!userId) {
      console.warn('Cannot clear history: no user ID');
      return;
    }
    
    // Clear local history
    this.saveLocalHistory(userId, []);
    
    // If using JetStream, we can't easily delete messages
    // Instead, we could create a new consumer that starts from "now"
    if (natsClient.isConnected()) {
      try {
        const jsm = natsClient.jetstreamManager();
        const sanitizedUsername = this.sanitizeUsernameForConsumer(userId);
        
        // Delete the old consumer if it exists
        await jsm.consumers.delete(this.STREAM_NAME, `navigation-${sanitizedUsername}`)
          .catch(() => {
            // Consumer might not exist, that's fine
          });
          
        console.log(`Cleared JetStream history for user: ${userId}`);
      } catch (error) {
        console.error('Failed to clear JetStream history:', error);
      }
    }
    
    console.log(`Cleared history for user: ${userId}`);
  }
}
